import { Effect } from '@babylonjs/core/Materials/effect';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { SDF_LIB } from '@observant/shared';

import bgBody from '../shaders/background.fragment.glsl?raw';

const VERTEX = /* glsl */ `
precision highp float;
attribute vec3 position;
attribute vec2 uv;
uniform mat4 worldViewProjection;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = worldViewProjection * vec4(position, 1.0);
}`;

export interface Background {
  mesh: Mesh;
  update(time: number, aspect: number): void;
}

/** A large plane running the SDF/Tron backdrop, kept behind the meshes. The
 * caller repositions `mesh` to track the camera each frame (positioned in world
 * space rather than parented, since a Camera is not a transform node). */
export function createBackground(scene: Scene): Background {
  Effect.ShadersStore['observantBgVertexShader'] = VERTEX;
  // precision must precede the SDF_LIB helpers, or every float errors.
  Effect.ShadersStore['observantBgFragmentShader'] =
    `precision highp float;\n${SDF_LIB}\n${bgBody}`;

  const mat = new ShaderMaterial(
    'bg',
    scene,
    { vertex: 'observantBg', fragment: 'observantBg' },
    { attributes: ['position', 'uv'], uniforms: ['worldViewProjection', 'time', 'aspect'] },
  );
  mat.backFaceCulling = false;

  const mesh = CreatePlane('background', { width: 400, height: 240 }, scene);
  mesh.material = mat;
  mesh.isPickable = false;

  return {
    mesh,
    update(time, aspect) {
      mat.setFloat('time', time);
      mat.setFloat('aspect', aspect);
    },
  };
}
